import { advancedSearchApi, handleError } from '@ardoq/api';
import { QueryBuilderQuery } from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import { ObservableState } from '@ardoq/rxbeach';
import { scopeDataOperations } from '@ardoq/scope-data';
import { isEqual } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { toScopeDataWithSortedComponentsAndWorkspacesByNames } from './toScopeDataWithSortedComponentsAndWorkspacesByNames';
import { Locale } from '@ardoq/locale';
import { EnhancedScopeData } from '@ardoq/data-model';
import { LoadErrorMessageKey, LoadingMessageKey } from './loadStatusKey';

type QuerySource = {
  query: QueryBuilderQuery | null;
};

export const getScopeDataStream = <T extends QuerySource>(
  querySource$: Observable<T> | ObservableState<T>,
  locale$: Observable<Locale>
) => {
  const query$: Observable<QueryBuilderQuery | null> = querySource$.pipe(
    map(({ query }) => query),
    distinctUntilChanged((a, b) => isEqual(a, b)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const loadScopeData$ = query$.pipe(
    switchMap(query =>
      query
        ? advancedSearchApi.scopeData({ query, minimal: true })
        : of(scopeDataOperations.getEmpty())
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const loadingScopeDataNotification$: Observable<LoadingMessageKey | null> =
    merge(
      query$.pipe(map((): LoadingMessageKey | null => 'hierarchy')),
      loadScopeData$.pipe(map((): LoadingMessageKey | null => null))
    ).pipe(startWith<LoadingMessageKey | null>(null));

  const loadScopeDataError$: Observable<LoadErrorMessageKey | null> = merge(
    query$.pipe(map((): LoadErrorMessageKey | null => null)),
    loadScopeData$.pipe(
      filter(isArdoqError),
      map((): LoadErrorMessageKey | null => 'hierarchy')
    )
  ).pipe(startWith<LoadErrorMessageKey | null>(null));

  const enhancedScopeData$: Observable<EnhancedScopeData> = combineLatest([
    loadScopeData$.pipe(handleError()),
    locale$,
  ]).pipe(
    map(([scopeData, locale]) => {
      const sortedScopeData =
        toScopeDataWithSortedComponentsAndWorkspacesByNames(scopeData, locale);
      return scopeDataOperations.enhance(sortedScopeData);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return {
    loadingScopeDataNotification$,
    loadScopeDataError$,
    enhancedScopeData$,
  };
};
